import React, { useMemo, useRef, useState } from "react";
import { styled } from "styled-components";
import { ConfigSchema, SchemaField, VehicleRow } from "./configTypes";
import {
  buildCreatePayload,
  canSetOnCreate,
  Draft,
  DraftValue,
  FieldGroup,
  groupFields,
  isFixedAfterCreate,
  missingRequired,
  requiredOnCreate,
  validateAll,
  validateDraft,
  validateVehicleName,
} from "./configModel";
import { createVehicle, FleetStateApiError } from "./configApi";
import FieldControl from "./FieldControl";
import { tokens } from "./tokens";
import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

/**
 * Onboard a tractor.
 *
 * Until `POST /v1/vehicles` existed, the Google Sheet was the only thing that
 * could mint a roster row — which is what made retiring the sheet impossible.
 * This is the console's replacement for that path.
 *
 * The form is schema-driven like the rest of the console: every editable field
 * is offered, grouped and ordered by the schema, and which ones are *required*
 * comes from `required_on_create` rather than a list kept here. Only the roster
 * name is special-cased, because it is the partition key rather than a field.
 * The fields that can never be changed afterwards (identity) come first, right
 * under the name: this form is the one chance to set them.
 *
 * Deliberately permissive beyond those required few. The record this console is
 * replacing is scattered across Slack threads, spreadsheets and documents, so
 * the first job is getting a tractor *in* — a form that demanded a complete
 * config record would just send people back to the spreadsheet.
 *
 * Nothing is lost by accident. Escape and a click outside close the box only
 * while it is still empty; once anything has been typed, leaving takes the
 * Cancel button and a confirmation. The submit button is never disabled for
 * validity — a disabled button cannot be focused and cannot explain itself —
 * it answers a premature click with what is still needed instead.
 */
interface AddTractorDialogProps {
  schema: ConfigSchema;
  token: string;
  /** Roster names already in the fleet, so a duplicate is caught before POST. */
  existingNames: string[];
  onCreated: (row: VehicleRow) => void;
  onCancel: () => void;
}

const NAME_ID = "new-vehicle-name";
const NAME_LABEL = "Roster name";

/** "a", "a and b", "a, b and c". */
const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

/**
 * Focus a control by id. A short enum renders as a radiogroup whose id sits on
 * the wrapper, which is not itself focusable — so land on its first segment.
 */
const focusControl = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const target = el.matches("input, select, textarea, button")
    ? el
    : el.querySelector<HTMLElement>(
        "input, select, textarea, button, [tabindex]",
      );
  (target ?? el).focus();
};

interface Problem {
  /** The control's DOM id, so the first one can be focused. */
  id: string;
  label: string;
}

const AddTractorDialog: React.FC<AddTractorDialogProps> = ({
  schema,
  token,
  existingNames,
  onCreated,
  onCancel,
}) => {
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  /** Has Add been pressed? From then on the form says what is still missing. */
  const [attempted, setAttempted] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  // `canSetOnCreate`, not `can_edit`: identity is fixed once the row exists, so
  // onboarding is the only moment it can be supplied — and the form that mints
  // the row is the one place it must still be offered.
  const editable = useMemo(
    () => schema.fields.filter(canSetOnCreate),
    [schema],
  );

  // Identity first, in its own group, because this is the only time it can be
  // set; then everything else in the schema's order. Both keep the schema
  // group's heading and note.
  const groups = useMemo<FieldGroup[]>(() => {
    const identity = editable.filter(isFixedAfterCreate);
    const rest = editable.filter((f) => !isFixedAfterCreate(f));
    return [
      ...groupFields(identity, undefined, schema.groups),
      ...groupFields(rest, undefined, schema.groups),
    ];
  }, [editable, schema.groups]);

  const requiredLabels = useMemo(
    () => [
      NAME_LABEL,
      ...[...requiredOnCreate(editable)]
        .sort((a, b) => a.order - b.order)
        .map((f) => f.label),
    ],
    [editable],
  );

  const existing = useMemo(() => new Set(existingNames), [existingNames]);
  const trimmedName = name.trim();
  const duplicateError = existing.has(trimmedName)
    ? `${trimmedName} already exists — open it instead of creating a second one.`
    : null;
  const nameProblem = validateVehicleName(name) ?? duplicateError;
  const nameError = nameTouched ? nameProblem : null;

  const dirty =
    name !== "" ||
    Object.values(draft).some((v) =>
      Array.isArray(v) ? v.length > 0 : v !== "",
    );

  /** What stands between this form and a POST, in the order it is laid out. */
  const collectProblems = (fieldErrors: Record<string, string>): Problem[] => {
    const missing = new Set(
      missingRequired(editable, draft).map((f) => f.name),
    );
    const found: Problem[] = [];
    if (nameProblem) found.push({ id: NAME_ID, label: NAME_LABEL });
    for (const group of groups) {
      for (const field of group.fields) {
        if (missing.has(field.name) || fieldErrors[field.name]) {
          found.push({ id: `config-${field.name}`, label: field.label });
        }
      }
    }
    return found;
  };
  const problems = attempted ? collectProblems(errors) : [];

  const update = (fieldName: string, value: DraftValue) => {
    setDraft((prev) => ({ ...prev, [fieldName]: value }));
    const field = editable.find((f) => f.name === fieldName);
    if (!field) return;
    setErrors((prev) => {
      const next = { ...prev };
      const message = validateDraft(field, value);
      if (message) next[fieldName] = message;
      else delete next[fieldName];
      return next;
    });
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmingDiscard(true);
    else onCancel();
  };

  const submit = async () => {
    setNameTouched(true);
    setAttempted(true);
    const found = validateAll(editable, draft, canSetOnCreate);
    setErrors(found);
    const blocking = collectProblems(found);
    if (blocking.length) {
      focusControl(blocking[0].id);
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const created = await createVehicle(
        token,
        trimmedName,
        buildCreatePayload(editable, draft),
      );
      onCreated(created);
    } catch (err) {
      if (err instanceof FleetStateApiError) {
        setApiError(
          err.status === 409
            ? `A tractor named "${trimmedName}" already exists. Roster names are unique — open it instead of creating a second one.`
            : err.message,
        );
      } else {
        setApiError("Couldn’t reach the API. Nothing was created.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
    >
      <DialogContent
        className="max-w-[560px]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameRef.current?.focus();
        }}
        // A blank form can be dismissed casually; one with anything in it
        // cannot — that path goes through Cancel and a confirmation.
        onEscapeKeyDown={(e) => {
          if (saving || dirty) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (saving || dirty) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Add a tractor</DialogTitle>
          <DialogDescription>
            Creates the roster row the rest of the system keys on. Only{" "}
            {joinWithAnd(requiredLabels)}{" "}
            {requiredLabels.length === 1 ? "is" : "are"} required — everything
            else can be filled in later from the tractor’s page.
          </DialogDescription>
        </DialogHeader>

        <Body>
          <Field>
            <Label htmlFor={NAME_ID}>
              {NAME_LABEL} <Required>required</Required>
            </Label>
            <Input
              id={NAME_ID}
              ref={nameRef}
              $invalid={Boolean(nameError)}
              value={name}
              placeholder="dl13"
              aria-required="true"
              aria-invalid={Boolean(nameError)}
              aria-describedby={
                nameError ? `${NAME_ID}-error` : `${NAME_ID}-help`
              }
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
            />
            {nameError ? (
              <FieldError id={`${NAME_ID}-error`}>{nameError}</FieldError>
            ) : (
              <Hint id={`${NAME_ID}-help`}>
                The permanent key for this tractor, used in URLs and by the
                vehicle registry. It cannot be changed later.
              </Hint>
            )}
          </Field>

          {groups.map((group) => (
            <Group key={group.key}>
              {group.label && <GroupLabel>{group.label}</GroupLabel>}
              {group.note && (
                <GroupNote>
                  {/* The schema's note describes the record ("Read-only.
                      Identity is set when the tractor is onboarded…"); on the
                      form that does the onboarding the first sentence is
                      false, so it is dropped here. */}
                  {group.note.replace(/^Read-only\.\s*/, "")}
                </GroupNote>
              )}
              {group.fields.map((field) => (
                <FieldRow
                  key={field.name}
                  field={field}
                  value={draft[field.name] ?? ""}
                  error={errors[field.name]}
                  onChange={(next) => update(field.name, next)}
                />
              ))}
            </Group>
          ))}
        </Body>

        {apiError && <ErrorBox role="alert">{apiError}</ErrorBox>}

        <DialogFooter className="items-center">
          {problems.length > 0 && (
            <StillNeeded role="alert">
              Still needed: {problems.map((p) => p.label).join(", ")}
            </StillNeeded>
          )}
          <Button
            variant="ghost"
            size="md"
            onClick={requestClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Creating…" : "Add tractor"}
          </Button>
        </DialogFooter>

        <AlertDialog
          open={confirmingDiscard}
          onOpenChange={(next) => !next && setConfirmingDiscard(false)}
        >
          <AlertDialogContent>
            <AlertDialogTitle>Discard this tractor?</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing has been created yet. What you’ve entered here will be
              lost.
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              >
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                className={cn(
                  buttonVariants({ variant: "destructive", size: "sm" }),
                )}
                onClick={onCancel}
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Label, control, and the line under it, wired together for assistive tech:
 * the control is named by the label (`-label`), described by whichever of the
 * error (`-error`) or help (`-help`) line is showing, and marked required when
 * the schema says so. The "required" pill stays a visual cue; `aria-required`
 * is what a screen reader hears.
 */
const FieldRow: React.FC<{
  field: SchemaField;
  value: DraftValue;
  error?: string;
  onChange: (value: DraftValue) => void;
}> = ({ field, value, error, onChange }) => {
  const id = `config-${field.name}`;
  const describedBy = error
    ? `${id}-error`
    : field.help
      ? `${id}-help`
      : undefined;
  return (
    <Field>
      <Label id={`${id}-label`} htmlFor={id}>
        {field.label}
        {field.required_on_create && <Required>required</Required>}
      </Label>
      <FieldControl
        field={field}
        value={value}
        error={error}
        onChange={onChange}
        labelledBy={`${id}-label`}
        describedBy={describedBy}
        required={field.required_on_create}
      />
      {error ? (
        <FieldError id={`${id}-error`}>{error}</FieldError>
      ) : (
        field.help && <Hint id={`${id}-help`}>{field.help}</Hint>
      )}
    </Field>
  );
};

/** The scrolling region: the title and the buttons stay put around it. */
const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Group = styled.div`
  border-top: 1px solid ${tokens.line};
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const GroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.navy};
`;

const GroupNote = styled.p`
  margin: -8px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${tokens.slate};
`;

const Field = styled.div``;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${tokens.slate};
  margin-bottom: 5px;
`;

const Required = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${tokens.majorText};
  background: rgba(255, 150, 0, 0.12);
  border: 1px solid rgba(255, 150, 0, 0.35);
  border-radius: 4px;
  padding: 1px 5px;
`;

const Input = styled.input<{ $invalid: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-family: ${tokens.fontMono};
  font-size: 13.5px;
  color: ${tokens.ink};
  background: ${tokens.white};
  border: 1px solid ${(p) => (p.$invalid ? tokens.groundedDot : tokens.line)};
  border-radius: 8px;
  padding: 8px 10px;

  &:focus {
    outline: none;
    border-color: ${tokens.blue};
    box-shadow: 0 0 0 3px rgba(0, 105, 255, 0.12);
  }
`;

const Hint = styled.div`
  font-size: 11.5px;
  color: ${tokens.slate};
  margin-top: 4px;
`;

const FieldError = styled.div`
  font-size: 11.5px;
  color: ${tokens.groundedText};
  margin-top: 4px;
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  border: 1px solid rgba(229, 72, 77, 0.35);
  background: rgba(229, 72, 77, 0.08);
  color: ${tokens.groundedText};
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
`;

/** Sits beside the buttons, on the left, and says why Add did nothing. */
const StillNeeded = styled.div`
  margin-right: auto;
  font-size: 12.5px;
  line-height: 1.4;
  color: ${tokens.groundedText};
`;

export default AddTractorDialog;
